import { IApplicationRole } from '@cscglobal-tbs/react-components-core';
import { CorptaxUserMenu, ICorptaxUserMenuProps, IUserAccessDetails } from '@cscglobal-tbs/user-menu-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { getGetFavoriteItemsQueryKey, getGetNavigationItemsQueryKey } from '../../../api/navigation/navigation';
import {
    getGetCurrentApplicationRoleQueryKey,
    useGetApplicationRoles,
    useGetCurrentApplicationRole,
    usePutCurrentApplicationRole,
} from '../../../api/roles/roles';
import { useGetLicensedProducts, useGetUserAccessDetails } from '../../../api/user/user';
import { IUserMenuContainerProps } from './UserMenuContainer';

export interface IFullUserMenuContainerProps extends IUserMenuContainerProps {
    loginHintForIdp?: string;
}

export const FullUserMenuContainer = (props: IFullUserMenuContainerProps) => {
    const queryClient = useQueryClient();
    const applicationRolesQuery = useGetApplicationRoles();
    const applicationRoleCurrentQuery = useGetCurrentApplicationRole();
    const applicationRoleCurrentMutation = usePutCurrentApplicationRole();
    const userAccessDetailsQuery = useGetUserAccessDetails();
    const licensedProductsQuery = useGetLicensedProducts();
    const [errorDismissed, setErrorDismissed] = useState(false);

    const handleErrorDismissed = () => {
        setErrorDismissed(true);
    };

    const handleRoleSelected = (roleId: string) => {
        applicationRoleCurrentMutation.mutate(
            { data: { roleId } },
            {
                onSuccess: () => {
                    const navigationQueryKey = getGetNavigationItemsQueryKey();
                    const currentRole = getGetCurrentApplicationRoleQueryKey();
                    const favoritesQueryKey = getGetFavoriteItemsQueryKey();
                    // invalidating queries to ensure the new role is reflected in the UI
                    queryClient.invalidateQueries({ queryKey: navigationQueryKey });
                    queryClient.invalidateQueries({ queryKey: currentRole });
                    queryClient.invalidateQueries({ queryKey: favoritesQueryKey });
                },
                onError: () => {
                    // do not refetch the current query if it worked or a re-render happens
                    // which causes the user menu to close, which we don't want, since the error shows up
                    // in this control
                    setErrorDismissed(false);
                },
            }
        );
    };

    const getErrorText = () => {
        if (errorDismissed) {
            return undefined;
        }
        if (applicationRolesQuery.isError) {
            return 'Error loading roles';
        }
        if (applicationRoleCurrentQuery.isError) {
            return 'Error loading current role';
        }
        if (applicationRoleCurrentMutation.isError) {
            return 'Error updating current role';
        }
        if (userAccessDetailsQuery.isError) {
            return 'Error loading user access details';
        }
        if (licensedProductsQuery.isError) {
            // Safe default: hide PAT when we can't confirm licenses
            return undefined;
        }
        return undefined;
    };

    const PAT_LICENSE_GUID = '22a942e2-4cff-448d-855a-08b644b407bd';
    const isCorptaxPersonalAccessTokenLicensed =
        (licensedProductsQuery.data as Array<{ id?: string; licensed?: boolean }> | undefined)?.some(
            (p) => (p?.id === PAT_LICENSE_GUID || p?.id === String(PAT_LICENSE_GUID)) && (p?.licensed === true || p?.licensed === undefined)
        ) ?? false;

    const childProps: ICorptaxUserMenuProps = {
        ...props,
        isCorptaxPersonalAccessTokenLicensed,
        roles: (applicationRolesQuery.data as IApplicationRole[]) ?? [],
        defaultSelectedRoleId: applicationRoleCurrentQuery.data,
        userAccessDetails: (userAccessDetailsQuery.data as IUserAccessDetails) ?? undefined,
        onErrorDismissed: handleErrorDismissed,
        onRoleSelected: handleRoleSelected,
        errorText: getErrorText(),
    };

    return <CorptaxUserMenu {...childProps} />;
};
